import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  ChevronLeft,
  ChevronRight,
  Flex,
  Icon,
  Stack,
} from "@storyofams/react-ui";
import { createClient } from "@supabase/supabase-js";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { defineMessages, useIntl } from "react-intl";
import { useQuery } from "react-query";

import styled, { css } from "styled-components";
import { usePagination } from "~hooks";
import { Divider } from "../../../components";
import { Questions } from "./Questions";
import { Reviews as ReviewsSection } from "./Reviews";
import { Tabs } from "./Tabs";

const perPage = 5;

const messages = defineMessages({
  reviews: "Beoordelingen",
  writeReview: "Schrijf een review",
  questions: "Vragen",
  noReviews: "Geen beoordelingen",
  loadMore: "Load more",
  basedOnNReviews: "Gebaseerd op {count} beoordelingen",
});

export const Reviews = ({ propertys, productId, productTitle }) => {
  const intl = useIntl();
  const { query } = useRouter();
  const [isScrolled, setIsScrolled] = useState(false);
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState<any>();
  const [skinType, setSkinType] = useState<any>();
  const [purposeState, setPurpose] = useState<any>();
  const [bodyPart, setBodyPart] = useState<any>();
  const [sortingState, setSortingState] = useState({
    value: "videosFileName",
    asc: false,
    label: "Meest relevant",
  });
  const [sortingQuestionsState, setSortingQuestionsState] = useState({
    value: "dateCreated",
    asc: false,
    label: "Nieuw - oud",
  });
  const [starsFilter, setStarsFilter] = useState<any>();
  const [searchState, setSearchState] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const starsMemo = useCallback(
    (value) => setStarsFilter(value),
    [starsFilter]
  );
  const bodyPartMemo = useCallback((value) => setBodyPart(value), [bodyPart]);
  const skinTypeMemo = useCallback((value) => setSkinType(value), [skinType]);
  const questionSortingMemo = useCallback(
    (value) => setSortingQuestionsState(value),
    [sortingQuestionsState]
  );
  const ageMemo = useCallback((value) => setAge(value), [age]);
  const searchMemo = useCallback(
    (value) => setSearchState(value),
    [searchState]
  );
  const purposeMemo = useCallback((value) => setPurpose(value), [purposeState]);
  const id = useMemo(
    () => productId.slice(productId.lastIndexOf("/") + 1),
    [productId]
  );
  const supabase = createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL,
    process.env.NEXT_PUBLIC_SUPABASE_KEY
  );
  const scrollToReviewsSection = () => {
    const el = document?.getElementById("reviews");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      setIsScrolled(true);
    }
  };
  useEffect(() => {
    if (query?.section === "reviews" && !isScrolled) {
      scrollToReviewsSection();
    }
  }, [query?.section, query?.submit]);
  useEffect(() => {
    const el = document?.getElementById("reviews");
    if (el && query.tab === "reviews") {
      setTimeout(
        () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
        1000
      );
    }
  }, [query?.tab]);
  const { data: score } = useQuery(
    ["productScore", productId],
    async () => {
      const {
        data: [score],
      } = await supabase.rpc("product_full_stats", {
        p_productid: id,
      });
      return score;
    },
    { refetchOnWindowFocus: false }
  );
  const { data: reviews, refetch } = useQuery(
    [
      "reviews",
      id,
      searchState,
      age,
      skinType,
      purposeState,
      starsFilter?.value,
      sortingState.label,
    ],
    async () => {
      try {
        setIsLoading(true);
        const start = perPage * (page - 1);
        const end = start + perPage - 1;
        //@ts-ignore
        let query = supabase
          .from("product_reviews")
          .select(
            "*, customer_profiles!inner(email, skinType, skinCondition, age)",
            {
              count: "exact",
            }
          )
          .range(start, end)
          .eq("published", true)
          .eq("productId", id)
          .order(sortingState.value, {
            ascending: sortingState.asc,
            nullsFirst: false,
          });
        if (sortingState.value === "videosFileName") {
          query.order("imagesFileName", {
            ascending: sortingState.asc,
            nullsFirst: false,
          });
          query.order("created_at", {
            ascending: false,
          });
        }

        if (age) query.ilike("customer_profiles.age", age.value);
        if (skinType) query.ilike("customer_profiles.skinType", skinType.value);
        if (bodyPart) query.ilike("bodyParts", bodyPart.value);
        if (starsFilter) query.eq("rating", starsFilter.value);
        if (purposeState)
          query.ilike(
            "customer_profiles.skinCondition",
            `%${purposeState.value}%`
          );
        if (searchState) {
          query = query.or(
            `title.ilike.%${searchState}%,body.ilike.%${searchState}%,reply.ilike.%${searchState}%`
          );
        }
        const { count, data, error } = await query;
        console.log(error);
        setIsLoading(false);
        return { count, data };
      } catch {
        setIsLoading(false);
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  useEffect(() => {
    setPage(1);
  }, [tab, age, skinType, purposeState]);
  useEffect(() => {
    if (query?.tab === "questions") setTab(1);
  }, [query]);
  const StyledButton = styled(Flex)<{ isActive?: boolean }>`
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    font-size: 14px;
    border-radius: 50%;
    color: ${(p) => p.theme.colors.grey900};

    ${(p) =>
      p.isActive &&
      css`
        color: ${(p) => p.theme.colors.grey100};
        background-color: ${(p) => p.theme.colors.grey900};
      `}
  `;
  const setFilterMemo = useCallback(
    (obj) => setSortingState(obj),
    [sortingState]
  );
  const { data: questions, refetch: refetchQuestions } = useQuery(
    ["questions", id, searchState],
    async () => {
      try {
        setIsLoading(true);
        const start = perPage * (page - 1);
        const end = start + perPage - 1;
        let query = supabase
          .from("product_questions")
          .select("*", { count: "exact" })
          .range(start, end)
          .eq("isPublished", true)
          .eq("productId", id)
          .order(sortingQuestionsState.value, {
            ascending: sortingQuestionsState.asc,
            nullsFirst: false,
          });
        if (searchState) {
          query = query.or(
            `answer.ilike.%${searchState}%,body.ilike.%${searchState}%`
          );
        }
        const { count, data } = await query;
        setIsLoading(false);
        return { count, data };
      } catch {
        setIsLoading(false);
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  const {
    setPage,
    page,
    goToPreviousPage,
    goToNextPage,
    totalPages,
    paginationItems,
  } = usePagination(
    !tab || tab === 0 ? Number(reviews?.count) : Number(questions?.count),
    5,
    true
  );
  useEffect(() => {
    refetch();
    refetchQuestions();
  }, [page]);
  useEffect(() => setPage(1), [sortingState, starsFilter]);
  const renderLoadMoreButton = (props = {}) => (
    <Box mx="auto" mt={1}>
      <Stack space={1} width="100%" justifyContent="center">
        {!!paginationItems?.length && (
          <StyledButton
            onClick={() => {
              goToPreviousPage();
              const el = document?.getElementById("reviews");
              el.scrollIntoView({ behavior: "smooth" });
              // posthog.capture(
              //   tab === 0
              //     ? "Clicked Reviews Widget"
              //     : "Clicked Questions Widget",
              //   {
              //     section: "PaginationClicked",
              //     page,
              //   }
              // );
            }}
            as={page <= 1 ? "div" : "button"}
            border="1px solid grey900"
          >
            <Icon icon={ChevronLeft} width="5px" />
          </StyledButton>
        )}

        {paginationItems?.map((item, idx) => (
          <StyledButton
            key={item === "..." ? `${item}-${idx}` : item}
            onClick={(event) => {
              if (typeof item !== "string") {
                setPage(item);
                const el = document?.getElementById("reviews");
                el.scrollIntoView({ behavior: "smooth" });
                // posthog.capture(
                //   tab === 0
                //     ? "Clicked Reviews Widget"
                //     : "Clicked Questions Widget",
                //   {
                //     section: "PaginationClicked",
                //     page: item,
                //   }
                // );
              }
            }}
            isActive={page === item}
            as={typeof item === "string" ? "div" : "button"}
          >
            {item}
          </StyledButton>
        ))}

        <StyledButton
          onClick={() => {
            goToNextPage();
            const el = document?.getElementById("reviews");
            el.scrollIntoView({ behavior: "smooth" });
            // posthog.capture(
            //   tab === 0 ? "Clicked Reviews Widget" : "Clicked Questions Widget",
            //   {
            //     section: "PaginationClicked",
            //     page,
            //   }
            // );
          }}
          as={page >= totalPages ? "div" : "button"}
          border="1px solid grey900"
        >
          <Icon icon={ChevronRight} width="5px" />
        </StyledButton>
      </Stack>
    </Box>
  );
  return (
    <Box id="reviews" width="100%" css={{ "scroll-margin-top": "100px" }}>
      <Tabs
        width={"100%"}
        onClick={(idx) => {
          setPage(1);
          setTab(idx);
        }}
        activeTab={tab}
        tabs={[
          {
            label: intl.formatMessage(messages.reviews),
            amount: score?.review_count || 0,
          },
          {
            label: intl.formatMessage(messages.questions),
            amount: score?.question_count || 0,
          },
        ]}
      />
      <Divider />

      <Box width="100%">
        {tab === 0 && (
          <ReviewsSection
            propertys={propertys}
            bodyPart={bodyPart}
            reviews={reviews?.data}
            loadingReviews={isLoading}
            skinType={skinType}
            age={age}
            score={score}
            searchMemo={searchMemo}
            searchState={searchState}
            purposeState={purposeState}
            starsFilter={starsFilter}
            productId={id}
            starsMemo={starsMemo}
            bodyPartMemo={bodyPartMemo}
            productTitle={productTitle}
            skinTypeMemo={skinTypeMemo}
            setFilterMemo={setFilterMemo}
            sortingState={sortingState}
            ageMemo={ageMemo}
            purposeMemo={purposeMemo}
          >
            {!!reviews?.count && reviews?.count >= 5 && renderLoadMoreButton()}
          </ReviewsSection>
        )}

        {tab === 1 && (
          <Questions
            questions={questions?.data}
            productId={id}
            productTitle={productTitle}
            questionSortingMemo={questionSortingMemo}
            searchState={searchState}
            loadingQuestions={isLoading}
            sortingQuestionsState={sortingQuestionsState}
            setSearchMemo={searchMemo}
          >
            {!!questions?.count &&
              questions?.count >= 5 &&
              renderLoadMoreButton()}
          </Questions>
        )}
      </Box>
    </Box>
  );
};
